<template>
    <div class="ledger-view">
        <header class="ledger-header">
            <div class="ledger-header-text">
                <h2 class="ledger-title">Plan de cuentas</h2>
                <p class="ledger-subtitle">
                    Las cuentas permiten agrupar la data financiera de tu negocio.
                </p>
            </div>
            <div class="ledger-actions">
                <button
                    type="button"
                    class="ledger-action"
                    :disabled="loading || !planAccounts.length"
                    @click="downloadAllPlanAccounts"
                >
                    Exportar
                </button>
            </div>
        </header>

        <div v-if="needsReview" class="review-banner" role="status">
            <span class="review-banner-icon">
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                        d="M3 3v18h18 M7 16v-7 M12 16v-3 M17 16v-10"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    />
                </svg>
            </span>
            <div class="review-banner-text">
                <p class="review-banner-title">
                    En revisión por nuestro equipo de analistas
                </p>
                <p class="review-banner-subtitle">
                    Estamos revisando tu plan de cuenta antes de continuar…
                </p>
            </div>
        </div>

        <div v-if="loading" class="ledger-empty" role="status" aria-live="polite">
            Cargando plan de cuentas…
        </div>

        <div v-else-if="error" class="ledger-empty is-error" role="alert">
            No pudimos cargar el plan de cuentas. Reintentalo en unos minutos.
        </div>

        <div v-else-if="!planAccounts.length" class="ledger-empty">
            No hay plan de cuentas configurado todavía.
        </div>

        <ol v-else class="ledger-list" aria-label="Plan de cuentas">
            <li
                v-for="(planAccount, iPlan) in planAccounts"
                :key="planAccount.id"
                class="ledger-row level-1"
            >
                <button
                    type="button"
                    class="ledger-row-button"
                    :aria-expanded="String(planAccount.isOpen)"
                    @click="changeOpenView(iPlan, null)"
                >
                    <span class="ledger-code">{{ iPlan + 1 }}</span>
                    <span class="ledger-name">{{ planAccount.name }}</span>
                    <ArrowDown
                        class="ledger-chevron"
                        :class="{ 'is-rotated': planAccount.isOpen }"
                    />
                </button>

                <transition
                    name="ledger-expand"
                    @before-enter="onBeforeEnter"
                    @enter="onEnter"
                    @after-enter="onAfterEnter"
                    @leave="onLeave"
                >
                    <ol v-if="planAccount.isOpen" class="ledger-sublist">
                        <li
                            v-for="(subCuenta, iSub) in planAccount.subAccounts"
                            :key="subCuenta.id"
                            class="ledger-row level-2"
                            :class="{ 'is-headerless': subCuenta.name === 'Ventas' }"
                        >
                            <!-- Sub-account "Ventas" se renderiza sin header pero sus items
                                 quedan visibles bajo el plan-account padre (comportamiento legacy). -->
                            <button
                                v-if="subCuenta.name !== 'Ventas'"
                                type="button"
                                class="ledger-row-button"
                                :aria-expanded="String(subCuenta.isOpen)"
                                @click="changeOpenView(iPlan, iSub)"
                            >
                                <span class="ledger-code">{{ iPlan + 1 }}.{{ iSub + 1 }}</span>
                                <span class="ledger-name">{{ subCuenta.name }}</span>
                                <ArrowDown
                                    class="ledger-chevron"
                                    :class="{ 'is-rotated': subCuenta.isOpen }"
                                />
                            </button>

                            <transition
                                name="ledger-expand"
                                @before-enter="onBeforeEnter"
                                @enter="onEnter"
                                @after-enter="onAfterEnter"
                                @leave="onLeave"
                            >
                                <ol v-if="subCuenta.isOpen" class="ledger-sublist">
                                    <li
                                        v-for="(item, iItem) in subCuenta.items"
                                        :key="item.id"
                                        class="ledger-row level-3"
                                    >
                                        <div class="ledger-row-static">
                                            <span class="ledger-code">{{ iPlan + 1 }}.{{ iSub + 1 }}.{{ iItem + 1 }}</span>
                                            <span class="ledger-name">{{ item.name }}</span>
                                        </div>
                                    </li>
                                </ol>
                            </transition>
                        </li>
                    </ol>
                </transition>
            </li>
        </ol>
    </div>
</template>

<script>
import { get_account_plan_by_company_ordered } from "@/api/carteraAnalista";
import ArrowDown from "@/components/svg/ArrowDown.vue";

export default {
    name: "AccountsPlan",
    components: {
        ArrowDown,
    },
    data() {
        return {
            planAccounts: [],
            loading: true,
            error: null,
            needsReview: false,
        };
    },
    mounted() {
        get_account_plan_by_company_ordered({})
            .then(({ data }) => {
                this.planAccounts = this.buildPlanAccountsTree(data.account_plans);
                this.needsReview = Boolean(data.needs_account_plan_review);
            })
            .catch((err) => {
                this.error = err;
            })
            .finally(() => {
                this.loading = false;
            });
    },
    methods: {
        buildPlanAccountsTree(accountPlans) {
            const result = [];
            const lookup = {};
            accountPlans.forEach((row, i) => {
                if (!lookup[row.level_1]) {
                    lookup[row.level_1] = { name: row.level_1, id: i, isOpen: true, subAccounts: [], subLookup: {} };
                    result.push(lookup[row.level_1]);
                }
                const plan = lookup[row.level_1];
                if (!plan.subLookup[row.level_2]) {
                    plan.subLookup[row.level_2] = { name: row.level_2, id: i, isOpen: true, items: [] };
                    plan.subAccounts.push(plan.subLookup[row.level_2]);
                }
                plan.subLookup[row.level_2].items.push({ name: row.level_3, id: i });
            });
            // Limpiar lookups internos para no inflar el reactive tree.
            result.forEach((plan) => {
                delete plan.subLookup;
                plan.subAccounts.forEach((sub) => delete sub.subLookup);
            });
            return result;
        },
        changeOpenView(iPlan, iSub) {
            const target = iSub == null
                ? this.planAccounts[iPlan]
                : this.planAccounts[iPlan].subAccounts[iSub];
            target.isOpen = !target.isOpen;
        },
        async downloadAllPlanAccounts() {
            const XLSX = await import("xlsx");
            const data = [];
            this.planAccounts.forEach((plan) => {
                plan.subAccounts.forEach((subAccount) => {
                    subAccount.items.forEach((item) => {
                        data.push({
                            "Nivel 1": plan.name,
                            "Nivel 2": subAccount.name,
                            "Nivel 3": item.name,
                        });
                    });
                });
            });
            const worksheet = XLSX.utils.json_to_sheet(data);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Plan de cuentas");
            XLSX.writeFile(workbook, "Plan de cuentas.xlsx");
        },
        onBeforeEnter(el) {
            el.style.height = "0";
            el.style.opacity = "0";
        },
        onEnter(el, done) {
            el.offsetHeight;
            el.style.height = el.scrollHeight + "px";
            el.style.opacity = "1";
            const onEnd = () => {
                el.removeEventListener("transitionend", onEnd);
                done();
            };
            el.addEventListener("transitionend", onEnd);
        },
        onAfterEnter(el) {
            el.style.height = "auto";
        },
        onLeave(el, done) {
            el.style.height = el.scrollHeight + "px";
            el.offsetHeight;
            el.style.height = "0";
            el.style.opacity = "0";
            const onEnd = () => {
                el.removeEventListener("transitionend", onEnd);
                done();
            };
            el.addEventListener("transitionend", onEnd);
        },
    },
};
</script>

<style lang="scss" scoped>
.ledger-view {
    // Tokens scope-componente. Mismo dialecto que Sidebar (paper/rule/ink) más
    // surface-tier para la elevación inversa (header L1 más oscuro, hoja L3
    // más clara — los padres son rótulos, las hojas son data).
    --paper: #ffffff;
    --paper-soft: #fafbfc;
    --paper-emphasis: #f4f5f7;
    --paper-emphasis-hover: #eceef1;
    --rule: rgba(16, 24, 40, 0.08);
    --rule-strong: rgba(16, 24, 40, 0.12);
    --ink: #1d2939;
    --ink-muted: #475467;
    --ink-faint: #98a2b3;
    --accent: #0d5154;
    --accent-wash: rgba(13, 81, 84, 0.06);
    --hover: rgba(16, 24, 40, 0.03);
    --radius: 6px;
    // Banner "en revisión": verde mint (lo nuevo del onboarding).
    --review-bg: #e7f7ee;
    --review-border: #bfe8cf;

    padding-top: 24px;
    padding-right: clamp(16px, 5vw, 72px);
    padding-bottom: 48px;
    // El outer main.dashboard ya reserva var(--app-sidebar-w) para el rail.
    // Acá sólo damos respiración de contenido (margen visual desde el edge
    // del sidebar al primer carácter).
    padding-left: clamp(16px, 2vw, 32px);
    background-color: var(--paper-soft);
    min-height: 100vh;
    color: var(--ink);
    font-family: Inter, "Open Sans", sans-serif;
}

.ledger-header {
    position: sticky;
    top: var(--app-chrome-top, 0px);
    z-index: 5;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 24px;
    padding: 16px 0 16px;
    margin-bottom: 16px;
    background-color: var(--paper-soft);
    border-bottom: 1px solid var(--rule);
}

.ledger-header-text {
    flex: 1;
    min-width: 0;
}

.ledger-title {
    margin: 0 0 4px;
    font-size: 20px;
    font-weight: 700;
    line-height: 28px;
    letter-spacing: -0.01em;
    color: var(--ink);
}

.ledger-subtitle {
    margin: 0;
    font-size: 13px;
    line-height: 20px;
    color: var(--ink-muted);
}

.ledger-actions {
    flex-shrink: 0;
    padding-top: 4px;
}

.ledger-action {
    padding: 8px 14px;
    background-color: var(--paper);
    border: 1px solid var(--rule);
    border-radius: var(--radius);
    color: var(--ink);
    font-family: inherit;
    font-size: 13px;
    font-weight: 500;
    line-height: 20px;
    cursor: pointer;
    transition: background-color 0.15s ease, border-color 0.15s ease;

    &:hover:not(:disabled) {
        background-color: var(--hover);
        border-color: var(--rule-strong);
    }

    &:focus-visible {
        outline: 2px solid var(--accent);
        outline-offset: 2px;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
}

.ledger-empty {
    padding: 32px 20px;
    background-color: var(--paper);
    border: 1px solid var(--rule);
    border-radius: var(--radius);
    text-align: center;
    color: var(--ink-muted);
    font-size: 14px;
    line-height: 20px;

    &.is-error {
        color: var(--ink);
    }
}

.review-banner {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 16px;
    padding: 16px 20px;
    background-color: var(--review-bg);
    border: 1px solid var(--review-border);
    border-radius: 12px;
}

.review-banner-icon {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    background-color: var(--paper);
    border-radius: 10px;
    color: var(--accent);

    svg {
        width: 22px;
        height: 22px;
    }
}

.review-banner-title {
    color: var(--ink);
    font-size: 15px;
    font-weight: 700;
    line-height: 20px;
}

.review-banner-subtitle {
    color: var(--ink-muted);
    font-size: 13px;
    line-height: 18px;
}

.ledger-list {
    list-style: none;
    margin: 0;
    padding: 0;
    background-color: var(--paper);
    border: 1px solid var(--rule);
    border-radius: var(--radius);
    overflow: hidden;
}

.ledger-sublist {
    list-style: none;
    margin: 0;
    padding: 0;
    overflow: hidden;
}

.ledger-row {
    list-style: none;
    border-top: 1px solid var(--rule);

    &:first-child {
        border-top: none;
    }
}

.ledger-row-button,
.ledger-row-static {
    width: 100%;
    display: grid;
    grid-template-columns: 64px 1fr auto;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    background-color: transparent;
    border: none;
    text-align: left;
    font-family: inherit;
    color: inherit;
}

.ledger-row-button {
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
        background-color: var(--hover);
    }

    &:focus-visible {
        outline: 2px solid var(--accent);
        outline-offset: -2px;
    }
}

// Signature: row-index column. Tabular numerals, faint ink, ancho fijo.
// La numeración existía en el template como texto inline; acá la elevamos a
// columna propia con tratamiento tipográfico del libro mayor.
.ledger-code {
    font-size: 12px;
    font-weight: 500;
    color: var(--ink-faint);
    font-variant-numeric: tabular-nums;
    letter-spacing: 0.02em;
    white-space: nowrap;
}

.ledger-name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.ledger-chevron {
    width: 16px;
    height: 16px;
    color: var(--ink-faint);
    transition: transform 0.2s ease;

    &.is-rotated {
        transform: rotate(180deg);
    }
}

// Elevación invertida: padre más oscuro = rótulo, hoja más clara = data.
// Tres niveles de surface separados por whisper-quiet shifts.
.level-1 > .ledger-row-button {
    background-color: var(--paper-emphasis);

    .ledger-name {
        font-size: 15px;
        font-weight: 700;
        color: var(--ink);
    }

    &:hover {
        background-color: var(--paper-emphasis-hover);
    }
}

.level-2 > .ledger-row-button {
    background-color: var(--paper-soft);
    padding-left: 32px;

    .ledger-name {
        font-size: 14px;
        font-weight: 600;
        color: var(--ink);
    }
}

.level-3 > .ledger-row-static {
    background-color: var(--paper);
    padding-left: 48px;

    .ledger-name {
        font-size: 13px;
        font-weight: 500;
        color: var(--ink-muted);
    }
}

.level-2 .ledger-code {
    min-width: 56px;
}

.level-3 .ledger-code {
    min-width: 48px;
}

// "Ventas" no renderiza su header; el border-top del li level-2 dejaría una
// línea visual sin razón. Lo suprimimos para que sus items fluyan directo bajo
// el header level-1.
.level-2.is-headerless {
    border-top: none;
}

.ledger-expand-enter-active,
.ledger-expand-leave-active {
    transition: height 0.2s ease, opacity 0.2s ease;
}
</style>
